import { createContext } from 'react';
import { useRefs } from '@/scripts/hooks';
import { exportApis, pushLog } from '@/scripts/utils/devUtils';
import { setInitialed } from '@/configs/apis/user';
import { devStorage } from '@/configs/storages/browser';
import { useChatMessages } from './scripts/hooks/useChatMessages';
import { useChats } from './scripts/hooks/useChats';
import { useChatMediaCall } from './scripts/hooks/useChatMediaCall';
import { useQuickVO } from './scripts/hooks/useQuickVO';
import { useSingleEncryption } from './scripts/hooks/useSingleEncryption';
import { useGroupEncryption } from './scripts/hooks/useGroupEncryption';
import { useChatGroup } from './scripts/hooks/useChatGroup';
import type { UseChatsValueResult } from './scripts/context/chats';
import { useChatsValue } from './scripts/context/chats';
import type { UseChatMessagesValueResult } from './scripts/context/messages';
import { useChatMessagesValue } from './scripts/context/messages';
import type { UseChatSingleMessagesValueResult } from './scripts/context/messages-single';
import { useChatSingleMessagesValue } from './scripts/context/messages-single';
import type { UseChatGroupMessagesValueResult } from './scripts/context/messages-group';
import { useChatGroupMessagesValue } from './scripts/context/messages-group';
import type { UseChatMediaValueResult } from './scripts/context/media';
import { useChatMediaValue } from './scripts/context/media';
import type { UseSettingsPushValueResult } from './scripts/context/push-settings';
import { useSettingsPushValue } from './scripts/context/push-settings';
import type { UseChatGroupResult } from './scripts/hooks/useChatGroup/types';
import type { UseChatMediaCallResult } from './scripts/hooks/useChatMediaCall/types';
import type { ChatMessageMessageIdParams } from './scripts/hooks/useChatMessages/types';
import {
  ChatMessagePulled,
  type ChatMessage,
  type ChatMessagesLoadParams,
  type RetryParams,
  type SendParams,
  type UseChatMessagesResult,
} from './scripts/hooks/useChatMessages/types';
import type {
  Chat,
  ChatChatId,
  ChatParams,
  ChatQuery,
  CreateChatParams,
} from './scripts/hooks/useChats/types';
import { ChatCategory, type UseChatsResult } from './scripts/hooks/useChats/types';
import type { UseSingleEncryptionResult } from './scripts/hooks/useSingleEncryption/types';
import type { UseSingleChatsValueResult } from './scripts/context/chats-single';
import { useSingleChatsValue } from './scripts/context/chats-single';
import type { UseGroupEncryptionResult } from './scripts/hooks/useGroupEncryption/types';
import type { UseGroupChatsValueResult } from './scripts/context/chats-group';
import { useGroupChatsValue } from './scripts/context/chats-group';
import type { UseChatSocketResult } from './scripts/hooks/useChatSocket/types';
import { useChatSocket } from './scripts/hooks/useChatSocket';
import type { UseQuickVOResult } from './scripts/hooks/useQuickVO/types';
import type { UseChatMediaSingleValueResult } from './scripts/context/media-single';
import { useChatMediaSingleValue } from './scripts/context/media-single';
import type { UseChatMediaGroupValueResult } from './scripts/context/media-group';
import { useChatMediaGroupValue } from './scripts/context/media-group';
import { useFileMessages } from './scripts/hooks/useFileMessages';
import type { UseFileMessages } from './scripts/hooks/useFileMessages/types';

export type ChatContextValue = ReturnType<typeof useChatContextValue>;

export const chatContext = createContext<ChatContextValue>({} as never);

export interface ChatContextHooks {
  singleEncryption: UseSingleEncryptionResult;
  chatSocket: UseChatSocketResult;
  groupEncryption: UseGroupEncryptionResult;
  chats: UseChatsResult;
  chatMessages: UseChatMessagesResult;
  chatGroup: UseChatGroupResult;
  chatMediaCall: UseChatMediaCallResult;
  chatMediaQuickVO: UseQuickVOResult;
  fileMessages: UseFileMessages;
}

export interface ChatContextHooksFull extends ChatContextHooks {
  chatsValue: UseChatsValueResult;
  singleChatsValue: UseSingleChatsValueResult;
  groupChatsValue: UseGroupChatsValueResult;
  chatMessagesValue: UseChatMessagesValueResult;
  chatGroupMessagesValue: UseChatGroupMessagesValueResult;
  chatSingleMessagesValue: UseChatSingleMessagesValueResult;
  chatMediaValue: UseChatMediaValueResult;
  chatMediaSingleValue: UseChatMediaSingleValueResult;
  chatMediaGroupValue: UseChatMediaGroupValueResult;
  settingsPushValue: UseSettingsPushValueResult;
}

export interface ChatContextHooksProps {
  hooks: ChatContextHooks;
}

export function useChatContextValue() {
  // Base hooks
  const singleEncryption = useSingleEncryption();
  const groupEncryption = useGroupEncryption();
  const chatSocket = useChatSocket();
  const chatMessages = useChatMessages();
  const chats = useChats({ singleEncryption, chatMessages, groupEncryption });
  const chatGroup = useChatGroup({ chats, chatMessages, groupEncryption });
  const chatMediaCall = useChatMediaCall();
  const chatMediaQuickVO = useQuickVO({ chatMediaCall });
  const fileMessages = useFileMessages();

  const hooks = {
    singleEncryption,
    chatSocket,
    groupEncryption,
    chats,
    chatMessages,
    chatGroup,
    chatMediaCall,
    chatMediaQuickVO,
    fileMessages,
  } as ChatContextHooksFull;

  const hooksProps: ChatContextHooksProps = { hooks };

  hooks.chatsValue = useChatsValue(hooksProps);
  hooks.singleChatsValue = useSingleChatsValue(hooksProps);
  hooks.groupChatsValue = useGroupChatsValue(hooksProps);
  hooks.chatMessagesValue = useChatMessagesValue(hooksProps);
  hooks.chatGroupMessagesValue = useChatGroupMessagesValue(hooksProps);
  hooks.chatSingleMessagesValue = useChatSingleMessagesValue(hooksProps);
  hooks.chatMediaValue = useChatMediaValue(hooksProps);
  hooks.chatMediaSingleValue = useChatMediaSingleValue(hooksProps);
  hooks.chatMediaGroupValue = useChatMediaGroupValue(hooksProps);
  hooks.settingsPushValue = useSettingsPushValue(hooksProps);

  const {
    chatsValue,
    singleChatsValue,
    groupChatsValue,
    chatMessagesValue,
    chatGroupMessagesValue,
    chatSingleMessagesValue,
    chatMediaValue,
    chatMediaSingleValue,
    chatMediaGroupValue,
    settingsPushValue,
  } = hooks;

  const { reconnectCount, connect } = chatSocket;

  const { loading: singleLoading } = chatSingleMessagesValue;

  const { loading: groupLoading } = chatGroupMessagesValue;

  const [ready, setReady] = useState(false);
  // TODO amo: Use for Dev
  const encryptSetting = useEncryptionSetting();

  const loading = useMemo(
    () => singleLoading || groupLoading,
    [singleLoading, groupLoading]
  );

  const initChat = useCallback(
    async (params: ChatParams) => {
      return params.category === ChatCategory.SINGLE
        ? singleChatsValue.initSingleChat(params)
        : groupChatsValue.initGroupChat(params);
    },
    [groupChatsValue, singleChatsValue]
  );

  const createChat = useCallback(
    async (params: CreateChatParams) => {
      return 'userId' in params
        ? singleChatsValue.createSingleChat(params)
        : groupChatsValue.createGroupChat(params);
    },
    [groupChatsValue, singleChatsValue]
  );

  const createOrActiveChat = useCallback(
    async (params: CreateChatParams) => {
      const chat = await createChat(params);
      if (chat) {
        return await chats.activeChat(chat.chat_id);
      }
      return false;
    },
    [chats, createChat]
  );

  const loadChatByChatId = useCallback(
    async (params: ChatParams) => {
      return params.category === ChatCategory.SINGLE
        ? singleChatsValue.loadSingleChatByChatId(params)
        : groupChatsValue.loadGroupChatsByChatId(params);
    },
    [groupChatsValue, singleChatsValue]
  );

  const sendMessage = useCallback(
    async (params: SendParams) => {
      return params.chat.category === ChatCategory.SINGLE
        ? chatSingleMessagesValue.sendSingleMessage(params)
        : chatGroupMessagesValue.sendGroupMessage(params);
    },
    [chatGroupMessagesValue, chatSingleMessagesValue]
  );

  const retryMessage = useCallback(
    async (params: RetryParams) => {
      return params.chat.category === ChatCategory.SINGLE
        ? chatSingleMessagesValue.retrySingleMessage(params)
        : chatGroupMessagesValue.retryGroupMessage(params);
    },
    [chatGroupMessagesValue, chatSingleMessagesValue]
  );

  const loadMessages = useCallback(
    async (params: ChatMessagesLoadParams) => {
      return params.category === ChatCategory.SINGLE
        ? chatSingleMessagesValue.loadSingleMessages(params)
        : chatGroupMessagesValue.loadGroupMessages(params);
    },
    [chatGroupMessagesValue, chatSingleMessagesValue]
  );

  const pullMessages = useCallback(
    async (chat: Chat, params: ChatMessageMessageIdParams[]) => {
      const args = [
        {
          chat_id: chat.chat_id,
          messages: params,
        },
      ];

      const result = await (chat.category === ChatCategory.SINGLE
        ? chatSingleMessagesValue.pullSingleMessages(args)
        : chatGroupMessagesValue.pullGroupMessages(args));

      return result;
    },
    [chatGroupMessagesValue, chatSingleMessagesValue]
  );

  const value = useMemo(() => {
    const {
      initChat: _initChat,
      createChat: _createChat,
      loadChatByChatId: _loadChatByChatId,
      ...chatsValue_
    } = chatsValue;

    const {
      retryMessage: _retryMessage,
      loadMessages: _loadMessages,
      ...chatMessagesValue_
    } = chatMessagesValue;

    const { loading: _singleLoading, ...chatSingleMessagesValue_ } =
      chatSingleMessagesValue;

    const { loading: _groupLoading, ...chatGroupMessagesValue_ } =
      chatGroupMessagesValue;

    return {
      ready,
      loading,
      initChat,
      createChat,
      createOrActiveChat,
      loadChatByChatId,
      sendMessage,
      retryMessage,
      loadMessages,
      chatSocket,
      chatMessages,
      fileMessages,
      chatGroup,
      chatMediaCall,
      chatMediaQuickVO,
      singleEncryption,
      groupEncryption,
      groupChatsValue,
      singleChatsValue,
      pullMessages,
      ...encryptSetting,
      ...chats,
      ...chatsValue_,
      ...chatMessagesValue_,
      ...chatSingleMessagesValue_,
      ...chatGroupMessagesValue_,
      ...chatMediaValue,
      ...chatMediaSingleValue,
      ...chatMediaGroupValue,
      ...settingsPushValue,
    };
  }, [
    ready,
    chatsValue,
    chatMessagesValue,
    chatSingleMessagesValue,
    chatGroupMessagesValue,
    loading,
    initChat,
    createChat,
    createOrActiveChat,
    loadChatByChatId,
    sendMessage,
    retryMessage,
    loadMessages,
    chatSocket,
    chatMessages,
    fileMessages,
    chatGroup,
    chatMediaCall,
    chatMediaQuickVO,
    singleEncryption,
    groupEncryption,
    encryptSetting,
    chats,
    chatMediaValue,
    chatMediaSingleValue,
    chatMediaGroupValue,
    settingsPushValue,
    groupChatsValue,
    singleChatsValue,
    pullMessages,
  ]);

  useEffect(() => {
    pushLog(
      'state:',
      {
        chats: value.chatsState.chats,
        messages: value.chatMessages.messagesState.map,
        value,
      },
      false
    );
    exportApis({
      statusApis: value,
    });
  }, [value]);

  useEffect(() => {
    if (!loading) {
      setReady(true);
      setInitialed();
    }
  }, [loading]);

  useEffect(() => {
    let timerId: number;
    if (!loading && 0 < reconnectCount) {
      if (reconnectCount < 5) {
        connect();
      } else {
        timerId = window.setTimeout(() => {
          connect();
        }, 2000);
      }
    }
    return () => {
      clearTimeout(timerId);
    };
  }, [reconnectCount, loading, connect]);

  return value;
}

export function useChatContext() {
  return useContext(chatContext);
}

export function useChatMessagesMap({ chat_id }: ChatQuery) {
  const { chatMessages } = useChatContext();

  const chatMessagesMap = useMemo(
    () => chatMessages.messagesState.map[chat_id],
    [chat_id, chatMessages.messagesState.map]
  );

  return { chatMessagesMap };
}

export function useMessageChangeTrigger({ chat_id }: ChatQuery) {
  const [trigger, setTrigger] = useState<object>();
  const timerRef = useRef(0);

  const { chatMessagesMap } = useChatMessagesMap({ chat_id });

  useLayoutEffect(() => {
    const now = Date.now();
    if (now - timerRef.current > 200) {
      setTrigger({});
    }
  }, [chatMessagesMap?.messages]);

  return trigger;
}

export function useChatChangeTrigger(chat: Chat) {
  const { chat_id } = chat;
  const [trigger, setTrigger] = useState<object>();
  const timerRef = useRef(0);

  const { chatMessagesMap } = useChatMessagesMap({ chat_id });

  useLayoutEffect(() => {
    const now = Date.now();
    if (now - timerRef.current > 200) {
      setTrigger({});
      timerRef.current = now;
    }
  }, [chatMessagesMap?.messages, chat]);

  return useMemo(() => ({ trigger, setTrigger }), [trigger]);
}

export function useLastMessage(chat: Chat) {
  const { chat_id } = chat;

  const { trigger, setTrigger } = useChatChangeTrigger(chat);

  const { chatMessages, ready, pullMessages } = useChatContext();
  const [lastMessage, setLastMessage] = useState<ChatMessage>();

  const run = useCallback(async () => {
    if (ready) {
      let res = await chatMessages.getLastMessage(chat_id);

      if (res?.pulled === ChatMessagePulled.NO) {
        const { list } = await pullMessages(chat, [res]);
        if (list.length) {
          res = list[0].messages[0] as ChatMessage;
        } else {
          await chatMessages.deleteMessagesByIds([{ chat_id, messages: [res.id] }]);
          setTrigger({});
        }
      }

      setLastMessage(res);
    }
  }, [chat, chatMessages, chat_id, pullMessages, ready, setTrigger]);

  const refs = useRefs({
    run,
  });

  useEffect(() => {
    refs.current.run();
  }, [refs, ready, trigger]);

  return { lastMessage, trigger, run, setTrigger };
}

// TODO amo: Use for Dev
function useEncryptionSetting() {
  const initValue = useMemo(() => {
    return devStorage.getItem('encryptMsgValue') ?? false;
  }, []);

  const [encryptMsg, setEncryptMsg] = useState(initValue);

  useEffect(() => {
    devStorage.setItem('encryptMsgValue', encryptMsg);
  }, [encryptMsg]);
  return { encryptMsg, setEncryptMsg };
}

export function useMembersByChatId(chat_id: ChatChatId) {
  const {
    chatGroup: {
      chatGroupState: {
        state: { membersMap },
      },
    },
  } = useChatContext();

  const normalMemberList = useMemo(
    () => membersMap[chat_id]?.normalMembers || [],
    [membersMap, chat_id]
  );

  return normalMemberList;
}
